import { action, observable, computed, makeObservable } from 'mobx';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { ConversationSummary } from 'nomst/Stores/Decision/conversationSummary';
import { Amount } from 'nomst/Stores/Decision/amount';
import { StockAmount } from 'nomst/Stores/Decision/stockAmount';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { EmployeeDispatcher } from 'nomst/Stores/Decision/employee.dispatcher';
import { CountryBudgetDecisionDispatcher } from 'nomst/Stores/Decision/countryBudgetDecision.dispatcher';
import {
    PercentageCalculationRule
} from 'nomst/Stores/Configuration/Constants/percentageCalculationRule';
import { Range } from 'nomst/Stores/Decision/range';
import {
    AmountPayType
} from 'nomst/Stores/Configuration/Constants/amountPayType';
import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { EmployeeInfo } from 'nomst/Stores/Decision/employeeInfo';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import SafeMath from 'common/safeMath';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import {
    EmployeeClass,
    AmountCode,
    DataState,
    StockDisplayType,
    BaseOfCalculation,
    FeatureFlags
} from 'typings/enums';
import { IEmployee } from 'typings/employee';
import { PromotionRequest } from 'nomst/Stores/Decision/promotionRequest';
import { DataManagerFactory } from 'utils/MobxDataManager/dataManagerFactory';
import { CountryBudget } from './countryBudget';
import { IAmount } from 'typings/amount';
import { PayHistory } from './payHistory';
import { featureFlagHelper } from 'singletons';
import { IPromotionRequest } from 'typings/promotion';

export class Employee implements IEmployee {

    employeeSourceId: string;

    employeeClass: EmployeeClass;

    department: string;

    displayName: string;

    firstName: string;

    middleName: string;

    lastName: string;

    country: string;

    payEffectiveDate: Nullable<Date>;

    jobTitleInternal: string;

    jobCodeKey: string;

    jobCode: string;

    jobId?: number;

    plannedBonus: number;

    @observable hasPromotionRequest: boolean;

    @observable promotionRequest: PromotionRequest;

    compMemoPromotionRequest?: IPromotionRequest;

    promotionId: string;

    isoCurrencyCode: string;

    annualStandardWorkHours: number;

    fullTimeEquivalent: number;

    currentPay: number;

    annualizedCurrentPay: number;

    @observable hireDate: string;

    @observable workLocation: string;

    @observable employeeInfo: EmployeeInfo;

    bonus: number;

    commissionsPaid: number;

    totalAnnualShortTermIncentives: number;

    @observable isReviewable: boolean;

    @observable performanceRating: string;

    @observable currentIsoCurrencyCode: string;

    managerSourceId: string;

    bonusFieldDecisions: IBonusFieldRule[];

    meritRecommendation: number;

    plannedVariable: number;

    plannedStockOptionAmount: number;

    plannedStockOptionUnits: number;

    stockAmount: StockAmount;

    @observable payHistory: Nullable<PayHistory>;

    readonly conversationSummary: ConversationSummary;

    readonly range: Nullable<Range>;

    rangeDistribution?: number[];

    readonly employeeDispatcher: EmployeeDispatcher;

    private _amountsMap: Map<AmountCode, Amount>;

    private _budgetsMaps: Map<number, ICountryBudget>;

    private readonly _configuration: Configuration;

    private readonly _countryBudgetDecisionDispatcher: CountryBudgetDecisionDispatcher;

    private readonly _currencyConversion: CurrencyConversion;

    // tslint:disable-next-line
    constructor(
        employeeDto: IEmployee,
        annualStandardWorkHours: number,
        conversationSummaryDto: Nullable<IConversationSummary>,
        configuration: Configuration,
        decisionDispatcher: Nullable<DecisionDispatcher>,
        countryBudgetDecisionDispatcher: Nullable<CountryBudgetDecisionDispatcher>) {
        makeObservable(this);
        this._configuration = configuration;
        this._countryBudgetDecisionDispatcher = countryBudgetDecisionDispatcher ?? {} as CountryBudgetDecisionDispatcher;
        this.country = employeeDto.country ?? '';
        this.employeeSourceId = employeeDto.employeeSourceId;
        this.employeeClass = employeeDto.employeeClass ?? EmployeeClass.Annual;
        this.payEffectiveDate = employeeDto.payEffectiveDate ? new Date(employeeDto.payEffectiveDate) : null;
        this.displayName = employeeDto.displayName ?? '';
        this.firstName = employeeDto.firstName || '';
        this.middleName = employeeDto.middleName || '';
        this.lastName = employeeDto.lastName || '';
        this.jobTitleInternal = employeeDto.jobTitleInternal ?? '';
        this.jobCodeKey = employeeDto.jobCodeKey ?? '';
        this.jobCode = employeeDto.jobCode ?? '';
        this.jobId = employeeDto.jobId;
        this.hasPromotionRequest = employeeDto.hasPromotionRequest ?? false;
        this.promotionId = employeeDto.promotionId ?? '';
        this.isoCurrencyCode = employeeDto.isoCurrencyCode;
        this.currentIsoCurrencyCode = employeeDto.isoCurrencyCode;
        this.meritRecommendation = employeeDto.meritRecommendation || 0;
        this.annualStandardWorkHours = annualStandardWorkHours;
        this.plannedVariable = employeeDto.plannedVariable || 0;
        this.plannedStockOptionAmount = employeeDto.plannedStockOptionAmount || 0;
        this.plannedStockOptionUnits = employeeDto.plannedStockOptionUnits || 0;
        this.fullTimeEquivalent = employeeDto.fullTimeEquivalent ?? 0;
        this.currentPay = employeeDto.currentPay ?? 0;
        this.annualizedCurrentPay = employeeDto.annualizedCurrentPay ?? 0;
        this.hireDate = employeeDto.hireDate ?? '';
        this.workLocation = employeeDto.workLocation ?? '';
        this.bonus = employeeDto.bonus ?? 0;
        this.commissionsPaid = employeeDto.commissionsPaid ?? 0;
        this.totalAnnualShortTermIncentives = employeeDto.totalAnnualShortTermIncentives ?? 0;
        this.isReviewable = employeeDto.reviewStatus ?? false;
        this.performanceRating = employeeDto.performanceRating ?? '';
        this.managerSourceId = employeeDto.managerSourceId ?? '';
        this.department = employeeDto.department ?? '';
        this._currencyConversion = new CurrencyConversion(this._configuration.currencies);
        this.bonusFieldDecisions = employeeDto.bonusFieldDecisions ?? [];
        this.employeeDispatcher = new EmployeeDispatcher(this);
        this.rangeDistribution = employeeDto.rangeDistribution;

        this.setAmounts(employeeDto, decisionDispatcher);
        this.setBudgets(employeeDto);
        const defaultConversationSummary = {
            entityId: this.employeeSourceId,
            messageCount: 0,
            unreadMessageCount: 0
        } as IConversationSummary;

        this.conversationSummary = new ConversationSummary(
            conversationSummaryDto ? conversationSummaryDto : defaultConversationSummary,
            this.employeeSourceId,
            this.displayName,
            this._configuration);

        this.range = employeeDto.range ? new Range(
            employeeDto.range,
            this.isoCurrencyCode,
            this.isHourly,
            new CurrencyConversion(this._configuration.currencies)) : null;

        this.compMemoPromotionRequest = employeeDto.promotionRequest;

        this.employeeDispatcher = new EmployeeDispatcher(this);
    }

    @computed get isHourly(): boolean {
        return this.employeeClass === EmployeeClass.Hourly;
    }

    @computed get newPay(): number {
        return this.currentPay + this.totalNewPayAmounts;
    }

    @computed get annualizedNewPay(): number {
        const totalAnnualizedNewPayAmounts = this.isHourly ?
            this.totalNewPayAmounts * this.totalAnnualHours :
            this.totalNewPayAmounts;

        return this.annualizedCurrentPay + totalAnnualizedNewPayAmounts;
    }

    @computed get meritProratedIncrease(): number {
        const merit = this.amounts.find(amount =>
            amount.amountCode === AmountCode.Merit);

        if (!merit) {
            return 0;
        }

        return merit.proratedIncrease;
    }

    @computed get totalCombinedCompensation(): number {
        const {
            doesOtherCashUseTccField,
            doesCommissionsUseTccField,
            doesLastPaidStiUseTccField
        } = this._configuration.teamPageViews;

        const otherCash = doesOtherCashUseTccField ? this.totalAnnualShortTermIncentives : 0;
        const commissionsPaid = doesCommissionsUseTccField ? this.commissionsPaid : 0;
        const lastPaidSti = doesLastPaidStiUseTccField ?
            this.bonus : 0;

        const totalAmounts = this.amounts
            .filter(amount => this._configuration.teamPageViews.doesAmountUseTccField(amount.amountCode))
            .reduce<number>((accumulator, amount) => {
                const amountValue = amount.updateNewPay && this.isHourly ?
                    amount.value * this.totalAnnualHours :
                    amount.value;

                return accumulator + amountValue;
            }, 0);

        return this.annualizedCurrentPay
            + otherCash
            + commissionsPaid
            + lastPaidSti
            + totalAmounts;
    }

    @computed get convertedCurrentPay(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.currentPay);
    }

    @computed get convertedPromotionPay(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.getAmount(AmountCode.Promotion)?.value ?? 0);
    }

    @computed get convertedAnnualizedCurrentPay(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.annualizedCurrentPay);
    }

    @computed get convertedNewPay(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.newPay);
    }

    @computed get convertedAnnualizedNewPay(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.annualizedNewPay);
    }

    @computed get convertedTotalCombinedCompensation(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.totalCombinedCompensation);
    }

    get convertedBonus(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.bonus);
    }

    get convertedCommissionsPaid(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.commissionsPaid);
    }

    get convertedTotalAnnualShortTermIncentives(): number {
        return this._currencyConversion.convertCurrency(
            this.isoCurrencyCode,
            this.currentIsoCurrencyCode,
            this.totalAnnualShortTermIncentives);
    }

    get basePayForPayRange(): number {
        if (this._configuration.payRangeConfiguration.baseOfCalculation === BaseOfCalculation.BasePay) {
            return this.newPay;
        }

        return this.isHourly ? this.hourlyTotalCombinedCompensation : this.totalCombinedCompensation;
    }

    get amounts(): Amount[] {
        return Array.from(this._amountsMap.values());
    }

    get budgets(): ICountryBudget[] {
        return Array.from(this._budgetsMaps.values());
    }

    @action setReviewability(isReviewable: boolean): void {
        this.isReviewable = isReviewable;
    }

    @action changePerformanceRating(performanceRating: string): void {
        this.performanceRating = performanceRating;
    }

    calculatePayRange(job: IJobNoMst): void {
        const range = {
            minRange: job.minRange,
            midRange: job.midRange,
            maxRange: job.maxRange
        } as IJobNoMst;

        this.promotionRequest.proposedPayRange =
            new Range(range, this.currentIsoCurrencyCode, this.isHourly, this._currencyConversion);
        this.promotionRequest.proposedPayRange.setRangeIsoCurrencyCode(this.currentIsoCurrencyCode);
        if (this.isHourly) {
            this.promotionRequest.proposedPayRange.convertToHourly(
                this.currentPay, this.annualStandardWorkHours);
        } else {
            this.promotionRequest.proposedPayRange.convertToAnnual(
                this.currentPay, this.annualStandardWorkHours);
        }
    }

    getAmount(amountCode: AmountCode): NullOrUndefined<Amount> {
        return this._amountsMap.get(amountCode);
    }

    @computed
    get promotionAmount(): NullOrUndefined<Amount> {
        return this.getAmount(AmountCode.Promotion);
    }

    handleAutoCalculateLumpSum(amountCode: AmountCode): void {
        if (!this.isRecalculateLumpSumNeeded(amountCode)) {
            return;
        }

        const amount = this.getAmount(amountCode);
        const lumpSum = this.getAmount(AmountCode.LumpSum);
        if (!amount || !lumpSum) {
            return;
        }

        const previousAmountValue = amount.value;
        const previousLumpSumValue = lumpSum.value;
        this.transferOverPayToLumpSum(amountCode);
        this._countryBudgetDecisionDispatcher.dispatchAmountChanged(
            amount.amountCode,
            amount.amountIsoCurrencyCode,
            previousAmountValue,
            amount.value,
            amount.updateNewPay,
            this.isHourly,
            this.annualStandardWorkHours,
            this.fullTimeEquivalent);

        this._countryBudgetDecisionDispatcher.dispatchAmountChanged(
            lumpSum.amountCode,
            lumpSum.amountIsoCurrencyCode,
            previousLumpSumValue,
            lumpSum.value,
            lumpSum.updateNewPay,
            this.isHourly,
            this.annualStandardWorkHours,
            this.fullTimeEquivalent);
    }

    transferOverPayToLumpSum(amountCode: AmountCode): void {
        const amount = this.getAmount(amountCode);
        const lumpSum = this.getAmount(AmountCode.LumpSum);
        if (!amount || !lumpSum) {
            return;
        }

        const { baseOfCalculation } = this._configuration.payRangeConfiguration;
        if (baseOfCalculation === BaseOfCalculation.TCC) {
            this.transferOverPayToLumpSumBasedOnTcc(amount, lumpSum);
        } else if (baseOfCalculation === BaseOfCalculation.BasePay) {
            this.transferOverPayToLumpSumBasedOnNewPay(amount, lumpSum);
        }
    }

    isRecalculateLumpSumNeeded(amountCode: AmountCode): boolean {
        if (amountCode === AmountCode.LumpSum || amountCode === AmountCode.Promotion) {
            return false;
        }

        const lumpSumAmount = this.getAmount(AmountCode.LumpSum);
        if (lumpSumAmount && !lumpSumAmount.isEligible) {
            return false;
        }

        if (!this._configuration.useAutoCalculateLumpSum) {
            return false;
        }

        const { teamPageViews, payRangeConfiguration } = this._configuration;
        const amount = this.getAmount(amountCode);
        if (payRangeConfiguration.baseOfCalculation === BaseOfCalculation.TCC &&
            teamPageViews.doesAmountUseTccField(amount?.amountCode ?? {} as AmountCode)) {
            return true;
        } else if (payRangeConfiguration.baseOfCalculation === BaseOfCalculation.BasePay &&
            amount?.updateNewPay &&
            amount.amountCode === AmountCode.Merit) {
            return true;
        }

        return false;
    }

    setCurrentCurrency(currentIsoCurrencyCode: string): void {
        this.currentIsoCurrencyCode = currentIsoCurrencyCode ?
            currentIsoCurrencyCode :
            this.isoCurrencyCode;

        this.amounts.forEach(amount => amount.setCurrentCurrency(currentIsoCurrencyCode));
        if (this.stockAmount) {
            this.stockAmount.setCurrentCurrency(currentIsoCurrencyCode);
        }
    }

    setEmployeeInfo(employeeInfo: EmployeeInfo): void {
        this.employeeInfo = employeeInfo;
    }

    async getEmployeeInfo(masterAccountId: string): Promise<void> {
        if (this.employeeInfo) {
            return;
        }
        const employeeDataManager = dataManagerFactory.getEmployeeDataManager();
        employeeDataManager.setDataState(DataState.Pending);
        const employeeInfo = await employeeDataManager.getEmployeeBasicInfo(this.employeeSourceId, masterAccountId);
        this.setEmployeeInfo(employeeInfo);
        return employeeDataManager.setDataState(DataState.Idle);
    }

    @action
    async setPromotionRequest(
        promotionRequestDto: PromotionRequest,
        promotionAmount: NullOrUndefined<Amount>,
        testDataManager?: DataManagerFactory):
            Promise<void> {
        const promotionRequest = new PromotionRequest(
            promotionRequestDto,
            this._configuration,
            this.managerSourceId,
            new EmployeeDispatcher(this),
            promotionAmount,
            testDataManager);
        this.promotionRequest = promotionRequest;
        await this.promotionRequest.setOriginalPromotionData(promotionRequestDto, promotionAmount);
        this.promotionRequest.setPromotionAmount(this.promotionAmount);
    }

    @action
    async submitPromotionRequest(): Promise<void> {
        await this.promotionRequest.updatePromotionRequestDataEvent();
        this.hasPromotionRequest = true;
    }

    async getPromotionRequest(): Promise<void> {
        if (this.promotionRequest) {
            return;
        }
        const promotionRequestDataManager = dataManagerFactory.getPromotionRequestManager();
        const promotionAmount = this.getAmount(AmountCode.Promotion);
        if (this.hasPromotionRequest) {
            const promotionRequest = await promotionRequestDataManager.getPromotionRequestData(
                this._configuration.masterAccountId,
                this._configuration.cycleId,
                this.employeeSourceId,
                this.promotionId);
            if (!promotionRequest) {
                return;
            }
            promotionRequest.employeeSourceId = this.employeeSourceId;
            await this.setPromotionRequest(promotionRequest as PromotionRequest, promotionAmount);
        } else {
            await this.createNewPromotionRequest(promotionAmount ?? {} as Amount);
        }
        this.promotionRequest = this.promotionRequest as PromotionRequest;
        await this.promotionRequest.getPromotionJobList(this.jobCodeKey);


    }

    async createNewPromotionRequest(promotionAmount: Amount): Promise<void> {
        const newPromotion = {
            proposedJobCode: this.jobCode,
            proposedJobCodeKey: this.jobCodeKey,
            promotionId: this.promotionId,
            employeeSourceId: this.employeeSourceId
        } as PromotionRequest;
        await this.setPromotionRequest(newPromotion, promotionAmount);
        return this.promotionRequest.getPromotionJobList(this.jobCodeKey);
    }

    async saveNewPromotionRequest(): Promise<void> {
        const dataManager = dataManagerFactory.getPromotionRequestManager();
        dataManager.setDataState(DataState.Pending);
        await this.promotionRequest.createPromotionRequestDataEvent();
        await this.promotionRequest.saveComment();
        await this.promotionRequest.updatePromotionRequestDataEvent();
        dataManager.setDataState(DataState.Idle);
        this.hasPromotionRequest = true;
    }

    async deletePromotionRequest(): Promise<void> {
        const dataManager = dataManagerFactory.getPromotionRequestManager();
        dataManager.setDataState(DataState.Pending);
        await dataManager.deletePromotionRequest(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            this.employeeSourceId,
            this.promotionId);

        await this.promotionRequest.deletePromotionAmount();
        this.promotionRequest.clearData();
        dataManager.setDataState(DataState.Idle);
        this.hasPromotionRequest = false;
    }

    async initializePromotionRequest(): Promise<void> {
        if (this._configuration.isClosed ||
            !this.promotionAmount) {
            return;
        }
        const dataManager = dataManagerFactory.getPromotionRequestManager();
        dataManager.setDataState(DataState.Pending);
        await this.getPromotionRequest();
        await this.promotionRequest.getPromotionJobList(this.jobCodeKey);
        dataManager.setDataState(DataState.Idle);
    }

    async loadPayHistory(): Promise<void> {
        const showPayHistory = featureFlagHelper.featureFlagSettings[FeatureFlags.ShowPayHistory];
        if (!showPayHistory) {
            return;
        }

        if (this.payHistory) {
            return;
        }

        const payHistoryDataManager = dataManagerFactory.getPayHistoryDataManager();
        payHistoryDataManager.setDataState(DataState.Pending);
        const payHistoryDto =
            await payHistoryDataManager.getPayHistory(this._configuration.masterAccountId, this.employeeSourceId);
        this.payHistory = payHistoryDto ? new PayHistory(payHistoryDto, this._configuration.teamPageViews) : null;
        payHistoryDataManager.setDataState(DataState.Idle);
    }

    async saveEmployeeMultiFactorBonusDecision(
        masterAccountId: string,
        cycleId: number,
        budgetCycleId: number,
        employeeSourceId: string,
        bonusFieldDecisions: IBonusFieldRule[]): Promise<void> {
        const employeeDataManager = dataManagerFactory.getEmployeeDataManager();
        await employeeDataManager.saveEmpBonusFieldDecision(masterAccountId,
            employeeSourceId,
            cycleId,
            budgetCycleId,
            bonusFieldDecisions);

    }

    private setBudgets(employeeDto: IEmployee): void {
        this._budgetsMaps = new Map<number, CountryBudget>();
        Coalesce.array(employeeDto.budgets || []).forEach(budget => {
            this._budgetsMaps.set(
                budget.budgetCycleId,
                new CountryBudget(
                    budget,
                    this.isoCurrencyCode,
                    new CurrencyConversion(this._configuration.currencies)
                )
            );
        });
    }

    private setAmounts(employeeDto: IEmployee, decisionDispatcher: Nullable<DecisionDispatcher>): void {
        this._amountsMap = new Map<AmountCode, Amount>();
        let stockAmount: NullOrUndefined<StockAmount>;
        const amounts = employeeDto.amounts || [];
        Coalesce.array(amounts).forEach(amountDto => {
            const cycleBudgetField = this._configuration.getCycleBudgetField(amountDto.amountCode);
            switch (amountDto.amountCode) {
                case AmountCode.Stock:
                    stockAmount = this.getStockAmount(amountDto, cycleBudgetField, decisionDispatcher);
                    return;
                case AmountCode.Bonus:
                    this.plannedBonus = employeeDto.plannedBonus || 0;
                    this.bonusFieldDecisions = this.bonusFieldDecisions && this.bonusFieldDecisions.length
                        ? this.bonusFieldDecisions
                        : this._configuration.bonusFieldRules;
                    break;
            }
            this.createAmount(amountDto as Amount, cycleBudgetField, decisionDispatcher);
        });

        if (stockAmount) {
            this.stockAmount = stockAmount;
        }
    }

    private getStockAmount(
        amountDto: IAmount,
        cycleBudgetField: CycleBudgetField,
        decisionDispatcher: Nullable<DecisionDispatcher>): StockAmount {
        const targetAmount = cycleBudgetField.stockDisplayType === StockDisplayType.Unit
            ? this.plannedStockOptionUnits
            : this.plannedStockOptionAmount;
        return new StockAmount(
            amountDto,
            this.employeeSourceId,
            targetAmount,
            this.isoCurrencyCode,
            this.isoCurrencyCode,
            cycleBudgetField ? cycleBudgetField?.stockValue ?? 0 : 0,
            cycleBudgetField ? cycleBudgetField.stockDisplayType === StockDisplayType.Unit : false,
            new CurrencyConversion(this._configuration.currencies),
            this._configuration.homeCountryCurrency,
            decisionDispatcher ?? {} as DecisionDispatcher);
    }

    private createAmount(
        amountDto: Amount,
        cycleBudgetField: CycleBudgetField,
        decisionDispatcher: Nullable<DecisionDispatcher>): void {
        let targetAmount = 0;
        if (amountDto.amountCode === AmountCode.Bonus) {
            targetAmount = this.plannedBonus;
        } else if (amountDto.amountCode === AmountCode.VariablePay) {
            targetAmount = this.plannedVariable;
        } else if (amountDto.amountCode === AmountCode.Merit) {
            targetAmount = this.meritRecommendation;
        }

        this._amountsMap.set(
            amountDto.amountCode,
            new Amount(
                amountDto,
                this.employeeSourceId,
                this.currentPay,
                targetAmount,
                cycleBudgetField && cycleBudgetField.percentageCalculationRule ?
                    cycleBudgetField.percentageCalculationRule :
                    PercentageCalculationRule.PercentageOfCurrentPay,
                cycleBudgetField ? cycleBudgetField.payType : AmountPayType.None,
                this.isoCurrencyCode,
                this.isoCurrencyCode,
                new CurrencyConversion(this._configuration.currencies),
                decisionDispatcher,
                this.bonusFieldDecisions));
    }

    private ensureOverPaidValueIsClampedToZero(value: number): number {
        return value < 0 ? 0 : value;
    }

    private ensureOverPaidValueDoesNotExceedBudgetAmount(value: number, amount: Amount): number {
        return Math.min(value, amount.value);
    }

    private annualizeValue(value: number): number {
        return value * this.totalAnnualHours;
    }

    private transferOverPayToLumpSumBasedOnNewPay(amount: Amount, lumpSum: Amount): void {
        let overPayValue = this.newPay - this.lumpSumMaxValue;
        overPayValue = this.ensureOverPaidValueIsClampedToZero(overPayValue);

        overPayValue = this.ensureOverPaidValueDoesNotExceedBudgetAmount(overPayValue, amount);
        amount.setValue(amount.value - overPayValue);
        if (amount.updateNewPay && this.isHourly) {
            overPayValue = this.annualizeValue(overPayValue);
        }

        lumpSum.setValue(overPayValue);
    }

    private transferOverPayToLumpSumBasedOnTcc(amount: Amount, lumpSum: Amount): void {
        let overPayValue = this.tccWithoutLumpSum - this.annualLumpSumMaxValue;
        overPayValue = this.ensureOverPaidValueIsClampedToZero(overPayValue);
        if (amount.updateNewPay && this.isHourly) {
            let hourlyOverPayValue = SafeMath.divide(overPayValue, this.totalAnnualHours);
            hourlyOverPayValue = this.ensureOverPaidValueDoesNotExceedBudgetAmount(hourlyOverPayValue, amount);

            amount.setValue(amount.value - hourlyOverPayValue);
            overPayValue = this.annualizeValue(hourlyOverPayValue);
        } else {
            overPayValue = this.ensureOverPaidValueDoesNotExceedBudgetAmount(overPayValue, amount);
            amount.setValue(amount.value - overPayValue);
        }

        lumpSum.setValue(overPayValue);
    }

    private get tccWithoutLumpSum(): number {
        const { teamPageViews } = this._configuration;
        const lumpSum = this.getAmount(AmountCode.LumpSum);
        if (teamPageViews.doesAmountUseTccField(AmountCode.LumpSum) && lumpSum) {
            return this.totalCombinedCompensation - lumpSum.value;
        }

        return this.totalCombinedCompensation;
    }

    private get lumpSumMaxValue(): number {
        const cycleLumpSum = this._configuration.getCycleBudgetField(AmountCode.LumpSum);
        if (!cycleLumpSum || !cycleLumpSum.rangeEnforcement || !this.range) {
            return 0;
        }

        const { autoCalculatePercentage } = cycleLumpSum.rangeEnforcement;
        const autoCalculateLumpSumRatio = autoCalculatePercentage / 100;
        return this.range.maxRange * autoCalculateLumpSumRatio;
    }

    private get annualLumpSumMaxValue(): number {
        return this.isHourly ?
            this.lumpSumMaxValue * this.totalAnnualHours :
            this.lumpSumMaxValue;
    }

    private get totalNewPayAmounts(): number {
        return this.amounts.filter(amount => amount.updateNewPay).reduce<number>(
            (accumulator, amount) => accumulator + amount.value,
            0);
    }

    private get hourlyTotalCombinedCompensation(): number {
        return SafeMath.divide(this.totalCombinedCompensation, this.totalAnnualHours);
    }

    private get totalAnnualHours(): number {
        return this.annualStandardWorkHours * this.fullTimeEquivalent;
    }
}